import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  LuActivity,
  LuArrowRight,
  LuClock,
  LuCheckCircle2,
  LuSmartphone,
  LuCalendarDays,
} from 'react-icons/lu';
import { NAV_ITEM_ICONS, NAV_ITEMS, BREADCRUMB_LABELS } from '@/constant/layout/layout.constant';
import { AlertLevelIcon, LEVEL_COLOR } from '@/pages/Alert/AlertLevelIcon';
import {
  getModifiedDashboarddata,
  getVdiTransactionCount,
  getDashboardLoading,
} from '@/store/selectors/dashboard.selector';
import {
  getVdiTransactionData,
  getSmartCoolerProductData,
  getAlertData,
  clearDashboardData,
} from '@/store/slices/dashboard.slice';
import { getOperators } from '@/store/slices/operator.slice';
import { getProviders } from '@/store/slices/provider.slice';
import { getAlerts } from '@/store/slices/alert.slice';
import { getProviderList } from '@/store/selectors/providers.selector';
import { getOperatorDetail } from '@/store/selectors/operator.selector';
import { getAlertsList } from '@/store/selectors/alerts.selector';
import { DASHBOARD_PROPS } from '@/constant/dashboard/dashboard.constant';
import { titleCaseString } from '@/utils/stringFormatting';
import {
  getDefaultDashboardRange,
  getDateTime,
  getSelectedTimeZone,
  formatDateRangeSummary,
} from '@/utils/dateTime';
import { useDateFilter } from '@/context/dateFilter.context';
import { useTheme } from '@/context/theme.context';
import { pagination } from '@/constant/global/global.constant';
import {
  DashboardStatCard,
  DashboardChart,
} from '@/components/organism/Dashboard';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { IconBadge } from '@/components/atoms/IconBadge';
import PillCell from '@/components/organism/StatusPills/PillCell';
import { Text, Caption, Heading } from '@/components';
import { APP_BG, APP_BORDER, APP_HEX } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';
import { DASHBOARD_TEST_IDS as TID } from '@/constant/testIds/dashboard.testIds';
import {
  DashSkStyle,
  StatSkeleton,
  ChartSkeleton,
  AlertSectionSkeleton,
} from './DashboardSkeleton';
import { ROUTE_CONFIG } from '@/config/Routes/routes.config';

// ── Module-level constants (stable across renders) ────────────────────────────

const formatAlertType = (type: string) =>
  (type ?? '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const CHART_NAV_MAP: Record<string, { navItem: keyof typeof NAV_ITEM_ICONS; label: string; href: string }> = {
  mmsSales: { navItem: NAV_ITEMS.SALES, label: BREADCRUMB_LABELS.SALES, href: '/sales' },
  mmsMarkets: { navItem: NAV_ITEMS.MARKETS, label: BREADCRUMB_LABELS.MARKETS, href: '/markets' },
  mmsProducts: { navItem: NAV_ITEMS.PRODUCTS, label: BREADCRUMB_LABELS.PRODUCTS, href: '/products' },
  mmsDevices: { navItem: NAV_ITEMS.DEVICES, label: BREADCRUMB_LABELS.DEVICES, href: '/devices' },
};

const VDI_PREFERRED_ORDER = ['mmsSales', 'mmsMarkets', 'mmsProducts', 'mmsDevices'] as const;
type VdiKey = typeof VDI_PREFERRED_ORDER[number];

/** Stable map — avoids object recreation on every render */
const GROUP_BY_LABELS: Record<string, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  year: 'Yearly',
};

const getChartMeta = (key: string) => ({
  title: CHART_NAV_MAP[key]?.label ?? titleCaseString(key),
  icon: CHART_NAV_MAP[key]
    ? React.createElement(NAV_ITEM_ICONS[CHART_NAV_MAP[key].navItem], { className: 'w-4 h-4' })
    : undefined,
  href: CHART_NAV_MAP[key]?.href,
  subtitle:
    (DASHBOARD_PROPS.CHARTS.SUBTITLES as Record<string, string>)[key] ??
    DASHBOARD_PROPS.CHARTS.DEFAULT_SUBTITLE,
});

/** Pre-built chart meta for every VDI type — stable reference, no useMemo needed */
const VDI_CHART_METAS: Record<VdiKey, ReturnType<typeof getChartMeta>> = {
  mmsSales: getChartMeta('mmsSales'),
  mmsMarkets: getChartMeta('mmsMarkets'),
  mmsProducts: getChartMeta('mmsProducts'),
  mmsDevices: getChartMeta('mmsDevices'),
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const dispatch = useDispatch();
  const { filterValues, groupBy: activeGroupBy } = useDateFilter();
  const { colors } = useTheme();

  const data = useSelector(getModifiedDashboarddata, shallowEqual);
  const providerData = useSelector(getProviderList, shallowEqual);
  const operatorData = useSelector(getOperatorDetail, shallowEqual);
  const VdiCount = useSelector(getVdiTransactionCount);
  const alertsData = useSelector(getAlertsList, shallowEqual);
  const loading = useSelector(getDashboardLoading, shallowEqual);

  const isChartsLoading = loading?.vdiTransactions;  // overall flag — drives VDI stat-card only
  const vdiByType = loading?.vdiByType ?? {};          // per-chart flags
  const isProductsLoading = loading?.smartCoolerProducts;
  const isAlertsLoading = loading?.alerts;

  const selectedTimezone = getSelectedTimeZone();

  // Fall back to the default 7-day window when no filter has been applied yet.
  // Memoised so the default string values are stable across renders.
  const defaultDates = useMemo(
    () => getDefaultDashboardRange(selectedTimezone),
    [selectedTimezone],
  );

  // Effective date range as plain strings — primitive deps prevent spurious
  // effect re-fires when Header.tsx re-initialises filterValues to the same
  // default dates on mount (object reference changes, values do not).
  const effectiveStartDate = filterValues?.startDate
    ? String(filterValues.startDate)
    : defaultDates.startDate;
  const effectiveEndDate = filterValues?.endDate
    ? String(filterValues.endDate)
    : defaultDates.endDate;

  // Clear Redux state when navigating away from the dashboard
  useEffect(() => {
    return () => {
      dispatch(clearDashboardData());
    };
  }, [dispatch]);

  // VDI + Alerts — re-fire only when dates or groupBy granularity truly change.
  // Because deps are strings (not objects) this fires exactly once on mount and
  // again only when the user applies a different date range or groupBy.
  useEffect(() => {
    if (!effectiveStartDate || !effectiveEndDate) return;
    const payload = {
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
      selectedTimezone,
      groupBy: activeGroupBy,
    };
    dispatch(getVdiTransactionData(payload));
    dispatch(getAlertData(payload));
  }, [effectiveStartDate, effectiveEndDate, selectedTimezone, activeGroupBy, dispatch]);

  // Smart Cooler product count — not date-filtered, fetch once on mount
  useEffect(() => {
    dispatch(getSmartCoolerProductData({}));
  }, [dispatch]);

  // Operators, providers, recent alerts list — not date-filtered, fetch once on mount
  useEffect(() => {
    dispatch(getOperators(pagination));
    dispatch(getProviders(pagination));
    dispatch(getAlerts({ ...pagination, limit: 5 }));
  }, [dispatch]);

  // ── Derived display values ─────────────────────────────────────────────────
  const scTotal = data?.data?.smartCoolerProducts?.total ?? 0;
  const providersCount = providerData?.providers?.pagination?.totalRecords ?? 0;
  const operatorsCount = operatorData?.operators?.pagination?.totalRecords ?? 0;

  const recentAlerts = useMemo(
    () => (alertsData?.data ?? []).slice(0, 5),
    [alertsData],
  );

  const chartData = useMemo(
    () => (data?.data?.vdiTransactions ?? {}) as Record<string, any>,
    [data],
  );

  const dateRangeSummary = useMemo(
    () => formatDateRangeSummary(effectiveStartDate, effectiveEndDate, selectedTimezone),
    [effectiveStartDate, effectiveEndDate, selectedTimezone],
  );

  const dateLabel = dateRangeSummary || DASHBOARD_PROPS.PAGE.DATE_LABEL_DEFAULT;
  return (
    <div className="flex flex-col min-h-full">
      <div
        data-testid={tid(TID.PAGE)}
        className="flex flex-col w-full gap-5 pb-8"
        style={{ contain: 'layout style paint' }}
      >
        <DashSkStyle />

        {/* ── Page Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-3 pt-1">
          {/* Left: title + meta row */}
          <div className="flex flex-col gap-1.5 min-w-0">
            <Heading size="5xl" color="default" tracking="tight">
              {DASHBOARD_PROPS.PAGE.TITLE}
            </Heading>

            {/* Meta row: date-range pill + groupBy chip */}
            <div
              className="flex flex-wrap items-center gap-2"
              data-testid={tid(TID.DATE_LABEL)}
            >
              {/* Date range pill */}
              {dateLabel && (
                <span className={`
                  inline-flex items-center gap-1.5
                  px-2.5 py-1 rounded-lg
                  text-xs font-medium
                  border ${APP_BORDER.soft}
                  bg-gray-50 dark:bg-white/[0.04]
                  text-gray-600 dark:text-gray-400
                  select-none
                `}>
                  <LuCalendarDays size={11} className="shrink-0 opacity-70" />
                  {dateLabel}
                </span>
              )}

              {/* GroupBy chip */}
              {activeGroupBy && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border select-none"
                  style={{
                    color: colors.primary,
                    borderColor: `${colors.primary}30`,
                    background: `${colors.primary}0f`,
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: colors.primary }}
                  />
                  {GROUP_BY_LABELS[activeGroupBy]}
                </span>
              )}
            </div>
          </div>

        </div>

        {/* ── Stat Cards ──────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {isProductsLoading ? (
            <StatSkeleton delay={0} />
          ) : (
            <DashboardStatCard
              testId={tid(TID.STAT_SMART_COOLER_PRODUCTS)}
              title={BREADCRUMB_LABELS.SMART_COOLER_PRODUCTS}
              value={scTotal}
              icon={NAV_ITEM_ICONS[NAV_ITEMS.SMART_COOLER_PRODUCTS]}
              variant={1}
              href={`/${ROUTE_CONFIG.SMART_COOLER_PRODUCTS.path}`}
            />
          )}

          {isChartsLoading ? (
            <StatSkeleton delay={0.1} />
          ) : (
            <DashboardStatCard
              testId={tid(TID.STAT_VDI_TRANSACTIONS)}
              title={BREADCRUMB_LABELS.VDI_TRANSACTIONS}
              value={(VdiCount as number) ?? 0}
              icon={NAV_ITEM_ICONS[NAV_ITEMS.VDI_TRANSACTIONS]}
              variant={2}
              href={`/${ROUTE_CONFIG.VDI_TRANSACTIONS.path}`}
            />
          )}

          {operatorData?.loading ? (
            <StatSkeleton delay={0.2} />
          ) : (
            <DashboardStatCard
              testId={tid(TID.STAT_OPERATORS)}
              title={BREADCRUMB_LABELS.OPERATORS}
              value={operatorsCount}
              icon={NAV_ITEM_ICONS[NAV_ITEMS.OPERATORS]}
              variant={3}
              href={`/${ROUTE_CONFIG.OPERATORS.path}`}
            />
          )}
          {providerData?.loading ? (
            <StatSkeleton delay={0.3} />
          ) : (
            <DashboardStatCard
              testId={tid(TID.STAT_PROVIDERS)}
              title={BREADCRUMB_LABELS.PROVIDERS}
              value={providersCount}
              icon={NAV_ITEM_ICONS[NAV_ITEMS.PROVIDERS]}
              variant={4}
              href={`/${ROUTE_CONFIG.PROVIDERS.path}`}
            />
          )}
        </div>

        {/* ── Hero Chart (mmsSales) ───────────────────────────────────────────── */}
        <div
          style={{
            contain: 'layout paint style',
          }}
        >
          {/* skeleton while mmsSales is still loading; chart (or no-data state) once resolved */}
          {vdiByType[VDI_PREFERRED_ORDER[0]] !== false ? (
            <ChartSkeleton height={400} delay={0} barCount={24} />
          ) : (
            <DashboardChart
              testId={tid(TID.HERO_CHART)}
              chartKey={VDI_PREFERRED_ORDER[0]}
              title={VDI_CHART_METAS[VDI_PREFERRED_ORDER[0]]?.title}
              icon={VDI_CHART_METAS[VDI_PREFERRED_ORDER[0]]?.icon}
              subtitle={dateRangeSummary || VDI_CHART_METAS[VDI_PREFERRED_ORDER[0]]?.subtitle}
              data={chartData[VDI_PREFERRED_ORDER[0]]}
              height={300}
              href={VDI_CHART_METAS[VDI_PREFERRED_ORDER[0]]?.href}
              legendPosition="bottom"
            />
          )}
        </div>

        {/* ── Chart Grid + Recent Alerts ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Each of the 3 remaining VDI charts renders its own skeleton independently */}
          {VDI_PREFERRED_ORDER.slice(1).map((item, idx) =>
            vdiByType[item] !== false ? (
              // Still loading this specific type — show its own skeleton
              <ChartSkeleton key={item} height={288} delay={idx * 0.1} />
            ) : (
              // API resolved — DashboardChart shows data or its own no-data state
              <div
                key={item}
                style={{
                  contain: 'layout paint style',
                  animation: 'dashChartIn 0.22s ease both',
                  animationDelay: `${idx * 80}ms`,
                }}
              >
                <DashboardChart
                  testId={tid(TID.CHART(item))}
                  chartKey={item}
                  title={VDI_CHART_METAS[item as VdiKey]?.title}
                  icon={VDI_CHART_METAS[item as VdiKey]?.icon}
                  subtitle={dateRangeSummary || VDI_CHART_METAS[item as VdiKey]?.subtitle}
                  data={chartData[item]}
                  height={250}
                  href={VDI_CHART_METAS[item as VdiKey]?.href}
                  legendPosition="bottom"
                />
              </div>
            ),
          )}

          {/* Alerts chart — independent from VDI loading */}
          {isAlertsLoading ? (
            <ChartSkeleton height={288} delay={0.15} />
          ) : (
            <div
              style={{
                contain: 'layout paint style',
                animation: 'dashChartIn 0.22s ease both',
                animationDelay: `${VDI_PREFERRED_ORDER.slice(1).length * 80}ms`,
              }}
            >
              <DashboardChart
                testId={tid(TID.ALERTS_CHART)}
                chartKey="alerts"
                title={BREADCRUMB_LABELS.ALERTS}
                icon={React.createElement(NAV_ITEM_ICONS[NAV_ITEMS.ALERTS], { className: 'w-4 h-4' })}
                subtitle={dateRangeSummary || DASHBOARD_PROPS.CHARTS.ALERTS_SUBTITLE}
                data={data.data.alerts}
                height={250}
                href={`/${ROUTE_CONFIG.SMART_COOLER_ALERTS.path}`}
                legendPosition="bottom"
              />
            </div>
          )}

          {/* ── Recent Alerts ── spans 2 cols */}
          <div className="md:col-span-2 xl:col-span-2">
            {isAlertsLoading ? (
              <AlertSectionSkeleton rows={5} delay={0} />
            ) : (
              <SectionCard
                testId={tid(TID.RECENT_ALERTS_SECTION)}
                className="h-full"
                icon={
                  <span className="relative inline-flex">
                    {React.createElement(NAV_ITEM_ICONS[NAV_ITEMS.ALERTS], { size: 14 })}
                    {recentAlerts.some(
                      (a: any) =>
                        a?.status?.toUpperCase() ===
                        DASHBOARD_PROPS.RECENT_ALERTS.STATUS_OPEN,
                    ) && (
                        <span
                          className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-red-500 border ${APP_BORDER.dashDot} animate-pulse`}
                        />
                      )}
                  </span>
                }
                title={DASHBOARD_PROPS.RECENT_ALERTS.TITLE}
                subtitle={`${DASHBOARD_PROPS.RECENT_ALERTS.SUBTITLE_PREFIX} ${recentAlerts.length} ${DASHBOARD_PROPS.RECENT_ALERTS.SUBTITLE_SUFFIX}`}
                headerRight={
                  <Link
                    to={`/${ROUTE_CONFIG.SMART_COOLER_ALERTS.path}`}
                    className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold transition-opacity hover:opacity-60"
                    style={{ color: colors.primary }}
                  >
                    {DASHBOARD_PROPS.RECENT_ALERTS.VIEW_ALL}{' '}
                    <LuArrowRight size={9} />
                  </Link>
                }
                headerClassName={APP_BG.canvas}
                bodyBg={APP_BG.base}
                bodyClassName=""
              >
                {recentAlerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-6 gap-2">
                    <LuCheckCircle2
                      size={16}
                      className="text-gray-200 dark:text-gray-700"
                    />
                    <Caption weight="medium" color="faint">
                      {DASHBOARD_PROPS.RECENT_ALERTS.EMPTY}
                    </Caption>
                  </div>
                ) : (
                  <div className="flex flex-col divide-y divide-gray-50 dark:divide-white/[0.035]">
                    {recentAlerts.map((alert: any, idx: number) => {
                      const level = (alert?.level ?? 'INFO').toUpperCase();
                      const status = (alert?.status ?? '').toUpperCase();
                      const levelColor = LEVEL_COLOR[level] ?? APP_HEX.muted;

                      return (
                        <div
                          key={alert?.alertId ?? idx}
                          data-testid={TID.ALERT_ROW(idx)}
                          className="group grid grid-cols-[1fr_auto_auto] gap-x-2.5 items-center px-4 py-2.5 hover:bg-gray-50/80 dark:hover:bg-white/[0.02] transition-colors cursor-default"
                        >
                          {/* Left: icon + text */}
                          <div className="flex items-center gap-2 min-w-0">
                            <IconBadge
                              icon={
                                <AlertLevelIcon
                                  level={level}
                                  className="w-[18px] h-[18px]"
                                />
                              }
                              size="sm"
                              variant="custom"
                              customStyle={{ background: `${levelColor}18` }}
                              shadow={false}
                            />
                            <div className="min-w-0 flex flex-col justify-center">
                              <Text
                                as="p"
                                size="md"
                                weight="semibold"
                                color="soft"
                                truncate
                                className="leading-none"
                              >
                                {formatAlertType(alert?.type)}
                              </Text>
                              <Caption
                                size="xs"
                                color="muted"
                                truncate
                                className="flex items-center gap-1 leading-none mt-[3px]"
                              >
                                <LuClock size={7} className="shrink-0" />
                                {alert?.triggeredOn
                                  ? getDateTime(alert.triggeredOn)
                                  : '—'}
                                {alert?.deviceId && (
                                  <>
                                    <span className="opacity-30">·</span>
                                    <LuSmartphone size={7} className="shrink-0" />
                                    <span className="truncate max-w-[55px]">
                                      {alert.deviceId}
                                    </span>
                                  </>
                                )}
                              </Caption>
                            </div>
                          </div>

                          {/* Level pill */}
                          <PillCell
                            value={level}
                            icon={
                              <AlertLevelIcon level={level} className="w-3 h-3" />
                            }
                            customStyle={
                              levelColor
                                ? {
                                  backgroundColor: `${levelColor}18`,
                                  color: levelColor,
                                }
                                : undefined
                            }
                            config={
                              levelColor
                                ? { bg: '', text: '' }
                                : { bg: 'bg-gray-100', text: 'text-gray-500' }
                            }
                          />

                          {/* Status pill */}
                          <PillCell value={status} preset="alertStatus" dot />
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Dashboard);
